import { FormCheckboxGroup, useZionFormContext } from '@fs/zion-form'
import { ControlSplit } from '@fs/zion-icon'
import { Button, Divider, Separator } from '@fs/zion-ui'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSlsStateContext } from '@fs/zion-sls'
import PersonCheckboxField from './PersonCheckboxField'
import { useSharedBrowserStateContext } from '../../../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'
import { SELECTION_PREFIXES } from '../utils/formDefaults'

const { RECORD: RECORD_PREFIX, IMAGE: IMAGE_PREFIX, OTHER_ARKS: OTHER_ARKS_PREFIX } = SELECTION_PREFIXES
const CHECKBOX_GROUPS = [RECORD_PREFIX, IMAGE_PREFIX, OTHER_ARKS_PREFIX]

/**
 * Callback function for the View In Context button.
 *
 * @callback viewInContextHandler
 * @param {Number} pageNumber - image index + 1
 */

/**
 * GroupedPersonCheckboxes component for displaying checkboxes for three categories: in this record, in this image, and in other arks
 * @param {Array} personsInThisRecord - Array of persons in the current record
 * @param {Array} personsInThisImage - Array of persons in the current image
 * @param {Object} allTheArksExceptCurrent - Object containing keys for all other relevant arks
 * @param {Function} viewInContextHandler - called with pageNumber. Should display the corresponding image.
 * @param {String} selectedPersonId - The ID of the currently selected person
 * @param {Object} record - The record/household element containing the selected person
 * @returns {Element}
 */
export default function GroupedPersonCheckboxes({
  selectedPersonId,
  personsInThisRecord,
  personsInThisImage,
  allTheArksExceptCurrent,
  viewInContextHandler,
  record,
}) {
  const [t] = useTranslation()
  const zionSlsState = useSlsStateContext()
  const methods = useZionFormContext()
  const { thumbnails } = useSharedBrowserStateContext()

  // Build ark-to-index map once for performance
  const arkIndexMap = useMemo(() => {
    if (!thumbnails) return new Map()
    const map = new Map()
    thumbnails.forEach((thumbnail, index) => {
      map.set(thumbnail.ark, index)
    })
    return map
  }, [thumbnails])

  const currentArkIndex = arkIndexMap.get(zionSlsState.currentImageArk) ?? -1

  const onChange = useCallback(
    (personId, namePrefix) => {
      const values = methods.getValues()
      const newValue = values[namePrefix][personId]

      CHECKBOX_GROUPS.forEach((checkboxGroup) => {
        if (checkboxGroup !== namePrefix && values[checkboxGroup][personId] !== undefined) {
          methods.setValue(`${checkboxGroup}.${personId}`, newValue)
        }
      })
    },
    [methods]
  )

  return (
    <>
      <FormCheckboxGroup
        largerLabel
        label={t('record-image-checkboxes.in-household', 'In this Household')}
        data-testid="in-this-household-checkbox-group"
      >
        {personsInThisRecord.map((person) => (
          <PersonCheckboxField
            key={`inThisRecord.${person.id}`}
            person={person}
            namePrefix={RECORD_PREFIX}
            onChange={onChange}
            disabled={person?.id === selectedPersonId}
            record={record}
          />
        ))}
      </FormCheckboxGroup>
      <FormCheckboxGroup
        largerLabel
        label={t('record-image-checkboxes.in-image', 'In this Image')}
        data-testid="in-this-image-checkbox-group"
      >
        {personsInThisImage.map((person) => (
          <PersonCheckboxField
            key={`inThisImage.${person.id}`}
            person={person}
            namePrefix={IMAGE_PREFIX}
            onChange={onChange}
            disabled={person?.id === selectedPersonId}
          />
        ))}
      </FormCheckboxGroup>
      {allTheArksExceptCurrent &&
        Object.entries(allTheArksExceptCurrent)
          // This filter statement keeps us from attempting to load a page that isn't directly adjacent to current
          // This is necessary, because as of now, we don't load any other pages into state, so we don't have their data
          .filter(([ark]) => {
            const arkIndex = arkIndexMap.get(ark) ?? -1
            return Math.abs(currentArkIndex - arkIndex) === 1
          })
          .map(([ark, obj]) => {
            const arkIndex = arkIndexMap.get(ark) ?? -1
            const pageNumber = arkIndex + 1
            return (
              <React.Fragment key={pageNumber}>
                <Separator size="xs" />
                <Divider
                  label={t('record-image-checkboxes.names-on-page-n.label', 'Names on Page {pageNumber}', {
                    pageNumber,
                  })}
                />
                {viewInContextHandler && (
                  <Button Icon={ControlSplit} onClick={() => viewInContextHandler(pageNumber)} emphasis="medium">
                    {t('record-image-checkboxes.view-image-in-context.close.button', 'View in Context')}
                  </Button>
                )}
                {obj.display?.map((person) => (
                  <PersonCheckboxField
                    key={`inOtherArks.${person.id}`}
                    person={person}
                    namePrefix={OTHER_ARKS_PREFIX}
                    onChange={onChange}
                    disabled={person?.id === selectedPersonId}
                  />
                ))}
              </React.Fragment>
            )
          })}
    </>
  )
}
